import React, { FC, useState } from "react";
import { Box, Typography, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import { ReprintModal } from "../../../components/pos/reprint-modal/reprint-modal";
import { Colors } from "../../../configs";
import { IBill } from "../../../redux/actions/pintagAction";
import ViewBillModal from "../../../components/pos/view-bill-modal/ViewBillModal";
import { formatDate } from "../../../util/common-functions";

interface IPintagTableRowProps {
  lastRow: boolean;
  isSelected: boolean;
  bill: IBill;
  onAssignPintag: (row: IBill) => void;
}

const PintagTableRow: FC<IPintagTableRowProps> = ({
  lastRow,
  isSelected,
  bill,
  onAssignPintag,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reprintType, setReprintType] = useState<"invoice" | "pintag">("invoice");
  const [openModal, setOpenModal] = useState(false);
  const [openViewBill, setOpenViewBill] = useState(false);
  const [modalInvoiceNo, setModalInvoiceNo] = useState("");
  const [modalPinTagNo, setModalPinTagNo] = useState("");

  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement | SVGElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const openReprintModal = (type: "invoice" | "pintag") => {
    setReprintType(type);
    setModalInvoiceNo(String(bill.billId ?? ""));
    setModalPinTagNo(String(bill.pintagTags ?? ""));
    setOpenModal(true);
    handleMenuClose();
  };

  const openViewBillModal = () => {
    setOpenViewBill(true);
    handleMenuClose();
  };

  // Shared cell style — height must match heading cell height
  const cell: React.CSSProperties & Record<string, any> = {
    background: isSelected ? Colors.BROWN : Colors.ACCENT_GRAY,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "0.5rem 0.4rem",
    color: isSelected ? "#ffffff" : "#000000",
    overflow: "hidden",
    height: "2.92857rem",
    cursor: "pointer",
    minWidth: 0,
  };

  /*
    Returns a React Fragment — children become direct CSS Grid children in
    pintag-table.tsx, which guarantees pixel-perfect column alignment with
    the header row (they share the same grid template columns).

    MUI Modals use React Portals so they render in document.body, not inside
    the grid, even though they appear here in the JSX tree.
  */
  return (
    <>
      {/* Color */}
      <Box
        sx={{
          ...cell,
          padding: "0.5rem 0.5rem",
          ...(lastRow && { borderBottomLeftRadius: "0.71rem" }),
        }}
      >
        <Box
          sx={{
            width: "1.3rem",
            height: "1.3rem",
            borderRadius: "0.4rem",
            backgroundColor: bill.pintagColor || Colors.PINTAG5,
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Pintag */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.pintagTags || 0}</Typography>
      </Box>

      {/* Invoice */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.billId}</Typography>
      </Box>

      {/* Customer */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.customerId}</Typography>
      </Box>

      {/* Date In */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{formatDate(bill.createdTime)}</Typography>
      </Box>

      {/* Pickup Cashier */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.cashierName}</Typography>
      </Box>

      {/* Drop Cashier */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.dropCashier || "-"}</Typography>
      </Box>

      {/* Due Date */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.dueDate ? formatDate(bill.dueDate) : "-"}</Typography>
      </Box>

      {/* Pieces */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.totalItemCount}</Typography>
      </Box>

      {/* Rack */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.rackNumber ?? "-"}</Typography>
      </Box>

      {/* Pickup Date */}
      <Box sx={cell}>
        <Typography noWrap fontSize="0.85rem">{bill.lastPickupDate ? formatDate(bill.lastPickupDate) : "-"}</Typography>
      </Box>

      {/* Description */}
      <Box sx={{ ...cell, justifyContent: "flex-start", paddingLeft: "0.6rem" }}>
        <Typography noWrap fontSize="0.85rem">
          {(bill.description ?? "").trim().replace(/^\/+/, "").trim()}
        </Typography>
      </Box>

      {/* Action */}
      <Box
        sx={{
          ...cell,
          padding: "0.5rem 0.5rem",
          ...(lastRow && { borderBottomRightRadius: "0.71rem" }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <MoreVertIcon
          sx={{ cursor: "pointer", color: "inherit", fontSize: "1.4rem" }}
          onClick={handleMenuClick}
        />

        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem onClick={() => openReprintModal("invoice")}>Reprint Invoice</MenuItem>
          <MenuItem onClick={() => openReprintModal("pintag")}>Reprint Tag</MenuItem>
          <MenuItem onClick={openViewBillModal}>View Bill</MenuItem>
          <MenuItem onClick={() => { onAssignPintag(bill); handleMenuClose(); }}>Update Pintag</MenuItem>
          <MenuItem onClick={handleMenuClose}>Pickup Screens</MenuItem>
        </Menu>
      </Box>

      {/* Modals — rendered via MUI Portal into document.body, not into the grid */}
      {openModal && (
        <ReprintModal
          open={openModal}
          selectedTab={reprintType}
          invoiceNo={modalInvoiceNo}
          pinTagNo={modalPinTagNo}
          onClose={() => setOpenModal(false)}
        />
      )}
      {openViewBill && (
        <ViewBillModal
          open={openViewBill}
          onClose={() => setOpenViewBill(false)}
          bill={bill}
          billId=""
          selectedId={0}
        />
      )}
    </>
  );
};

export default PintagTableRow;
